import { Info } from "lucide-react";

import { createMDWrapper, getText, type Noticeboard } from "shared/lib";

import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components";
import {
  useExistingLandUses,
  useProjectDetails,
  useProposedLandUses,
} from "shared/hooks";
import { useAccount } from "@/hooks";
import { isEqual } from "lodash";

export interface ProposalProps {
  noticeboardData: Noticeboard | undefined | null;
}

export function Proposal({ noticeboardData }: ProposalProps) {
  const { conversation } = useAccount();

  const existingLandUses = useExistingLandUses(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const proposedLandUses = useProposedLandUses(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const { content: projectDetails } = useProjectDetails(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const renderExistingLandUse = () => {
    if (existingLandUses && existingLandUses.length > 0) {
      return (
        <ul className="[&>li:not(:last-child)]:mb-1">
          {existingLandUses.map((existingLandUse) => {
            return (
              <li key={existingLandUse}>
                <Badge
                  className="text-bodymd w-min font-medium whitespace-nowrap"
                  variant="outline"
                >
                  {existingLandUse}
                </Badge>
              </li>
            );
          })}
        </ul>
      );
    } else {
      return (
        <p className="text-textSubdued text-bodysm">
          {getText("Not yet determined.")}
        </p>
      );
    }
  };

  const renderProposedLandUse = () => {
    if (
      proposedLandUses.length > 0 &&
      !isEqual(proposedLandUses.sort(), existingLandUses.sort())
    ) {
      return (
        <ul className="[&>li:not(:last-child)]:mb-1">
          {proposedLandUses.map((proposedLandUse) => (
            <li key={proposedLandUse}>
              <Badge
                className="text-bodymd w-min font-medium whitespace-nowrap"
                variant="outline"
              >
                {proposedLandUse}
              </Badge>
            </li>
          ))}
        </ul>
      );
    } else if (
      proposedLandUses.length > 0 &&
      isEqual(proposedLandUses.sort(), existingLandUses.sort())
    ) {
      return (
        <Badge
          className="text-bodymd w-min font-medium whitespace-nowrap"
          variant="outline"
        >
          {getText("No change")}
        </Badge>
      );
    } else {
      return (
        <p className="text-textSubdued text-bodysm">
          {getText("Not yet determined.")}
        </p>
      );
    }
  };

  const getFactsAndQuestions = () => {
    if (projectDetails) {
      return (
        <span className="text-textPrimary text-bodysm">
          {createMDWrapper(projectDetails, {
            components: {
              h1({ children }) {
                return <h5 className="text-bodymd font-bold">{children}</h5>;
              },
              h2({ children }) {
                return <h5 className="text-bodymd font-bold">{children}</h5>;
              },
              h3({ children }) {
                return <h5 className="text-bodymd font-bold">{children}</h5>;
              },
              h4({ children }) {
                return <h5 className="text-bodymd font-bold">{children}</h5>;
              },
            },
          })}
        </span>
      );
    } else {
      return (
        <p className="text-textSubdued text-bodysm">
          {getText(
            "After answering more questions, a summary of your proposal will be shown here.",
          )}
        </p>
      );
    }
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-5">
        <h2 className="text-headinglg text-textPrimary">
          {getText("Proposal")}
        </h2>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <h3 className="text-headingsm">{getText("Existing land use")}</h3>

            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent className="flex flex-col gap-1">
                <p>{getText("How the lot is currently being used.")}</p>
                <p>
                  {getText(
                    "Land uses are described with specific terms that are defined in the planning scheme.",
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {renderExistingLandUse()}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <h3 className="text-headingsm">{getText("Proposed land use")}</h3>

            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent className="flex flex-col gap-1">
                <p>
                  {getText(
                    "How you are proposing to use the land in relation to its existing use.",
                  )}
                </p>
                <p>
                  {getText(
                    "Land uses are described with specific terms that are defined in the planning scheme.",
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {renderProposedLandUse()}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <h3 className="text-headingsm">{getText("Summary")}</h3>

            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent className="flex flex-col gap-1">
                <p>
                  {getText(
                    "How our AI has made sense of your proposal based on your answers.",
                  )}
                </p>
                <p>
                  {getText(
                    "If you see something that's not quite right, you can edit your proposal after all questions have been answered.",
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {getFactsAndQuestions()}
        </div>
      </div>
    </TooltipProvider>
  );
}
